import type { FC, ReactNode } from 'react'
import type { ChartAnimation } from '@/components/charts/chart-utils'

import { cn } from '@/lib/utils'
import { formatCompactCurrency, formatCurrency } from '@/lib/format'
import { MiniBars } from '@/components/charts/mini-bars'
import { Sparkline } from '@/components/charts/sparkline'
import { StackedBar } from '@/components/charts/stacked-bar'
import { DeltaBadge } from '@/components/charts/delta-badge'

import {
    CHART_COLORS,
    EXPENSE_CATEGORIES,
    EXPENSE_CHANGE,
    EXPENSE_SERIES,
    INCOME_CHANGE,
    INCOME_SERIES,
    INCOME_SOURCES,
    TOTAL_EXPENSES,
    TOTAL_INCOME
} from '@/constants/dashboard'

interface WidgetProps {
    animation?: ChartAnimation
    delay?: number
    legendCount?: number
    className?: string
}

const WIDGET_SURFACE = 'rounded-2xl border border-burgundy/10 bg-ivory/90 p-4 shadow-[0_24px_48px_-28px_rgb(57_26_24/0.45)] backdrop-blur-md'

export const ExpensesWidget: FC<WidgetProps> = ({
    animation = 'inview',
    delay = 0,
    legendCount = 2,
    className
}): ReactNode => {
    return (
        <div className={cn(WIDGET_SURFACE, className)}>
            <div className='flex items-center justify-between gap-3'>
                <p className='text-[13px] font-medium text-burgundy'>Total Expenses</p>
                <span className='font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>Sep 2026</span>
            </div>

            <div className='mt-2.5 flex items-end justify-between gap-4'>
                <div>
                    <p className='font-heading text-[1.7rem] font-medium leading-none tracking-[-0.03em] text-burgundy tabular-nums'>
                        {formatCurrency(TOTAL_EXPENSES)}
                    </p>

                    <p className='mt-2 flex items-center gap-1.5 text-[11px] text-stone'>
                        <DeltaBadge value={EXPENSE_CHANGE} good />
                        vs August
                    </p>
                </div>

                <MiniBars
                    values={EXPENSE_SERIES.slice(-7)}
                    color={CHART_COLORS.burgundy}
                    height={44}
                    animation={animation}
                    delay={delay}
                    className='w-24 shrink-0'
                />
            </div>

            <StackedBar
                title='Expenses by category'
                segments={EXPENSE_CATEGORIES}
                legendCount={legendCount}
                formatValue={formatCompactCurrency}
                animation={animation}
                delay={delay + 200}
                className='mt-4'
            />
        </div>
    )
}

export const IncomeWidget: FC<WidgetProps> = ({
    animation = 'inview',
    delay = 0,
    legendCount = 2,
    className
}): ReactNode => {
    return (
        <div className={cn(WIDGET_SURFACE, className)}>
            <div className='flex items-center justify-between gap-3'>
                <p className='text-[13px] font-medium text-burgundy'>Total Income</p>
                <span className='font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>Sep 2026</span>
            </div>

            <div className='mt-2.5 flex items-end justify-between gap-4'>
                <div>
                    <p className='font-heading text-[1.7rem] font-medium leading-none tracking-[-0.03em] text-burgundy tabular-nums'>
                        {formatCurrency(TOTAL_INCOME)}
                    </p>

                    <p className='mt-2 flex items-center gap-1.5 text-[11px] text-stone'>
                        <DeltaBadge value={INCOME_CHANGE} />
                        vs August
                    </p>
                </div>

                <Sparkline
                    values={INCOME_SERIES.slice(-7)}
                    color={CHART_COLORS.gold}
                    height={44}
                    animation={animation}
                    delay={delay}
                    className='w-24 shrink-0'
                />
            </div>

            <StackedBar
                title='Income by source'
                segments={INCOME_SOURCES}
                legendCount={legendCount}
                formatValue={formatCompactCurrency}
                animation={animation}
                delay={delay + 200}
                className='mt-4'
            />
        </div>
    )
}
